import type { Vec3 } from '../../geo/types';
import type { Rng } from '../../core/rng';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { transformDirection, transformPoint } from '../mesh/meshBuilder';
import { panelPoint } from './panels';
import { beamSolid, hexahedronSolid } from './solids';
import type { WallFace } from './houseFacades';
import type { Opening } from './openings';
import type { DetailSink } from './detailSink';
import type { HouseStreets } from './houseStreets';
import type { ShopFronts } from './shops';
import { shopShutterLines } from './openingDetail';
import { WARE_BASKET } from './wareBasket';

/**
 * What a shop shows of its wares, in the forms the Master of Alkmaar paints in panel 1 of the Seven
 * Works of Mercy (1504): a basket of loaves on a baker's counter; a row of long-handled iron implements
 * hung across the top of the next shop's opening; the baker's counter resting on a brick base under an
 * awning propped on struts. Alkmaar, 34 years before 1538, not Amsterdam (docs/plans/houses.md, "Wares").
 */
export interface ShopWares {
  readonly loaves: boolean;
  readonly implements: boolean;
  readonly brickCounter: boolean;
}

export const NO_WARES: ShopWares = { loaves: false, implements: false, brickCounter: false };

export const WARES = {
  /**
   * Food shops among the shops, from the food and the other markers of Lesger's 1585 map (S21, afb. 2)
   * on each street with nine or more; the rest of the city's lines together give `rest`.
   */
  food: {
    byStreet: {
      'street-nieuwendijk': 0.25,
      'street-warmoesstraat': 0.3,
      'street-kalverstraat': 0.7,
      'quay-oudezijds-voorburgwal': 0.65,
      'quay-nieuwezijds-voorburgwal': 0.6,
      'street-sint-jansstraat': 0.2,
      'street-nes': 0.6,
      'quay-nieuwezijds-achterburgwal': 0.5,
      'square-dam': 0.45,
      'quay-damrak-west': 0.45,
      'street-pijlsteeg': 0.2,
    } as Readonly<Record<string, number>>,
    rest: 0.55,
  },
  /** Of the shops selling other goods, those that hang the implements: no source counts them (L). */
  implements: 0.15,
  /** Of all shops, those whose counter rests on a brick base under an awning on struts (L). */
  brickCounter: 0.1,
  /** Where the basket stands: its middle this share across the opening, its back this far out. */
  basket: { ...WARE_BASKET, along: 0.6, out: 0.25 },
  implement: {
    spacing: 0.2,
    inset: 0.15,
    /** The rail under the opening's head, and how far out of the wall it and the implements hang. */
    rail: { drop: 0.12, half: 0.015 },
    out: 0.035,
    handle: { length: 0.42, half: 0.008 },
    blade: { width: 0.05, height: 0.1, half: 0.01 },
  },
  /** The counter overhangs its base by this much; the struts meet the awning this share of its way out. */
  base: { overhang: 0.05, sunk: 0.02 },
  strut: { half: 0.02, at: 0.8 },
} as const;

function sourceName(id: string): string {
  return id.replace(/-\d+$/, '');
}

export function foodShare(streets: HouseStreets): number {
  const id = streets.front?.id;
  if (!id) return WARES.food.rest;
  return WARES.food.byStreet[id] ?? WARES.food.byStreet[sourceName(id)] ?? WARES.food.rest;
}

/** From its own fork of the house's stream, so drawing the wares moves no other parameter of any house. */
export function sampleWares(shop: ShopFronts, streets: HouseStreets, rng: Rng): ShopWares {
  if (!shop.front) return NO_WARES;
  const r = rng.fork('wares');
  const loaves = r.chance(foodShare(streets));
  const hung = !loaves && r.chance(WARES.implements);
  const brickCounter = r.chance(WARES.brickCounter);
  return { loaves, implements: hung, brickCounter };
}

export interface WareStyles {
  readonly brick: VertexStyle;
  readonly shutter: VertexStyle;
  readonly iron: VertexStyle;
}

/** Brick as the side walls, whatever the front is built of: the Alkmaar baker's base is brick. */
export function wareStyles(styles: {
  readonly side: VertexStyle;
  readonly shutter: VertexStyle;
  readonly iron: VertexStyle;
}): WareStyles {
  return { brick: styles.side, shutter: styles.shutter, iron: styles.iron };
}

export interface WareContext {
  readonly builder: MeshBuilder;
  readonly frame: Frame;
  readonly details: DetailSink;
  readonly edgeId: number;
  readonly styles: WareStyles;
}

/** A point `out` in front of the face at (`along`, `up`) on it. */
function pointOn(face: WallFace, along: number, up: number, out: number): Vec3 {
  const p = panelPoint(face.panel, along, up);
  return [p[0] + face.outward[0] * out, p[1] + face.outward[1] * out, p[2] + face.outward[2] * out];
}

/** A box on the face from (x0, y0) to (x1, y1), from `out0` to `out1` in front of it. */
function faceBox(
  ctx: WareContext,
  style: VertexStyle,
  face: WallFace,
  [x0, x1]: readonly [number, number],
  [y0, y1]: readonly [number, number],
  [o0, o1]: readonly [number, number]
): void {
  const corners = Array.from({ length: 8 }, (_, i) =>
    pointOn(face, i & 1 ? x1 : x0, (i >> 1) & 1 ? y1 : y0, (i >> 2) & 1 ? o1 : o0)
  );
  hexahedronSolid(ctx.builder, style, ctx.frame, corners);
}

function placeLoaves(ctx: WareContext, face: WallFace, o: Opening): void {
  const { width, height, along, out } = WARES.basket;
  const origin = transformPoint(
    pointOn(
      face,
      o.x + along * o.width - width / 2,
      o.y + shopShutterLines(o.width, o.height).counterTop,
      out
    ),
    ctx.frame
  );
  const right = transformDirection(face.panel.uAxis, ctx.frame);
  const outward = transformDirection(face.outward, ctx.frame);
  ctx.details.add('loaves', { origin, right, outward, width, height }, ctx.edgeId);
}

function buildImplements(ctx: WareContext, face: WallFace, o: Opening): void {
  const { spacing, inset, rail, out, handle, blade } = WARES.implement;
  const top = o.y + o.height - rail.drop;
  const depth = [out - rail.half, out + rail.half] as const;
  faceBox(
    ctx,
    ctx.styles.iron,
    face,
    [o.x + inset - rail.half, o.x + o.width - inset + rail.half],
    [top - 2 * rail.half, top],
    depth
  );
  const count = Math.floor((o.width - 2 * inset) / spacing) + 1;
  const foot = top - 2 * rail.half - handle.length;
  for (let i = 0; i < count; i++) {
    const x = o.x + inset + i * spacing;
    faceBox(
      ctx,
      ctx.styles.iron,
      face,
      [x - handle.half, x + handle.half],
      [foot, top - 2 * rail.half],
      [out - handle.half, out + handle.half]
    );
    faceBox(
      ctx,
      ctx.styles.iron,
      face,
      [x - blade.width / 2, x + blade.width / 2],
      [foot - blade.height, foot],
      [out - blade.half, out + blade.half]
    );
  }
}

function buildBrickCounter(ctx: WareContext, face: WallFace, o: Opening): void {
  const lines = shopShutterLines(o.width, o.height);
  faceBox(
    ctx,
    ctx.styles.brick,
    face,
    [o.x + 2 * lines.jamb, o.x + o.width - 2 * lines.jamb],
    [0, o.y + lines.counterBottom],
    [-WARES.base.sunk, lines.reach - WARES.base.overhang]
  );
  const [headUp, headOut] = lines.awningAt(WARES.strut.at);
  const footUp = (lines.counterTop + lines.head) / 2;
  for (const x of [o.x + lines.jamb, o.x + o.width - lines.jamb]) {
    const foot = pointOn(face, x, o.y + footUp, WARES.strut.half);
    const head = pointOn(face, x, o.y + headUp - WARES.strut.half, headOut);
    beamSolid(ctx.builder, ctx.styles.shutter, ctx.frame, foot, head, WARES.strut.half);
  }
}

/** A shop's wares at each shop opening of its front; the basket is a LOD1 detail, the rest part of the house. */
export function placeWares(
  ctx: WareContext,
  face: WallFace,
  front: readonly Opening[],
  wares: ShopWares
): void {
  for (const o of front.filter((opening) => opening.kind === 'shop')) {
    if (wares.loaves) placeLoaves(ctx, face, o);
    if (wares.implements) buildImplements(ctx, face, o);
    if (wares.brickCounter) buildBrickCounter(ctx, face, o);
  }
}
